import { useCallback, useEffect, useMemo } from 'react'

import { useAuth0 } from '@auth0/auth0-react'

import appConfig, { AppNames } from 'config/appConfig'
import {
  allRoutesConfig,
  appAuth0LogoutCallbackPath,
  gotoAdminEntryPathFromEnduser,
  gotoBccLogout,
  gotoLoginAppLogout,
  gotoLegacyLogin
} from 'lib/routesConfig'
import { clearAllBccStorage } from 'lib/oauth2/bccOAuth2Utils'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { logout as appLogout, resetLogout } from 'redux/features/auth/authSlice'
import { isFailed, isSuccess } from 'redux/toolkit/api'
import { removeSessionCookie } from 'lib/cookies'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'
import { useIsAccountHelpdeskUser } from 'lib/hooks/useIsAccountHelpdeskUser'
import useHandleQNDigestLinks from 'lib/useHandleQNDigestLinks'
import useAppTypeEntryPaths from 'components/libs/routes/useAppTypeEntryPaths'
import { UserRole } from 'config/userRole'
import { isLoginAppEnabled } from 'lib/loginAppUtils'
import logger from './logger'

export type Logout = (useTDFTransition: boolean) => void
export type LogoutImpersonatedEnduser = () => void
// use this global variable to store the logout params because the some components are unmounted before the
//   useEffect part is able to catch the end of the app logout process.
export type IsTDFTransition = boolean
export type IsAzureUser = boolean
export type IsRolledOutOktaUser = boolean
export type ReturnToUrl = string | undefined
export type IsAccountAdmin = boolean
export type IsEnhancedAuth = boolean | undefined
export type IsLoginAppAuth = boolean

export enum LogoutTypes {
  'logout',
  'impersonatedEnduserLogout'
}

export type LogoutParameters =
  | [
      LogoutTypes,
      IsTDFTransition,
      IsAzureUser,
      IsRolledOutOktaUser,
      ReturnToUrl,
      IsAccountAdmin,
      IsEnhancedAuth,
      IsLoginAppAuth
    ]
  | undefined
let logoutParameters: LogoutParameters

const useLogout = (): [Logout, LogoutImpersonatedEnduser] => {
  const dispatch = useAppDispatch()
  const { logout: authLogout } = useAuth0()
  const [, , generateQNDigestReturnUrlParams] = useHandleQNDigestLinks()
  const isAccountHelpdeskUser = useIsAccountHelpdeskUser()
  const {
    appLogoutIsFinished,
    isAzureAdAccountStored,
    isPdDomainIdIsSet,
    roleType,
    isEnhancedAuthenticationEnabled,
    accessTokenObject,
    authMode,
    serverLoginAppEnabled
  } = useAppSelector(_store => ({
    appLogoutIsFinished: isSuccess(_store.auth.logoutApiStatus) || isFailed(_store.auth.logoutApiStatus),
    isAzureAdAccountStored: !!_store.auth.accessTokenObject?.isAzureAdAccount,
    isPdDomainIdIsSet: !!_store.auth.accessTokenObject?.pdDomainId,
    roleType: _store.auth.accessTokenObject?.roleType,
    isEnhancedAuthenticationEnabled: !!_store.app.publicAppSettings?.isEnhancedAuthenticationEnabled,
    accessTokenObject: _store.auth.accessTokenObject,
    authMode: _store.auth.accessTokenObject?.authMode,
    serverLoginAppEnabled: !!_store.app.publicAppSettings?.loginAppEnabled
  }))
  // Apply cookie override (login_app_override) for QA testing
  const loginAppEnabled = isLoginAppEnabled(serverLoginAppEnabled)
  const { appTypeLoginEntryPath } = useAppTypeEntryPaths()

  // app is logged out - session is invalidated on api-gateway
  useEffect(() => {
    if (logoutParameters && appLogoutIsFinished) {
      // collect and reset the logout params
      const [
        logoutType,
        isTDFTransition,
        isAzureUser,
        isRolledOutOktaUser,
        returnToUrl,
        isAccountAdmin,
        isEnhancedAuth,
        isLoginAppAuth
      ] = logoutParameters
      logoutParameters = undefined

      // eslint-disable-next-line default-case
      switch (logoutType) {
        case LogoutTypes.logout:
          // remove values from localstorage and reset the logout redux states
          dispatch(resetLogout())
          removeSessionCookie()

          // Note: Login App logout is handled synchronously in logout() callback above,
          // so isLoginAppAuth should always be false here. This is a safety fallback.
          if (isLoginAppAuth) {
            clearAllBccStorage()
            gotoLoginAppLogout()
            return
          }

          if (isAccountAdmin) {
            gotoBccLogout()
            return
          }

          if (isEnhancedAuth) {
            appTypeLoginEntryPath.goto()
            return
          }

          // invalidate the session in Okta when Azure logout and Auth0 is enabled
          if (appConfig.AUTH0.isEnabled && isAzureUser) {
            // keep the rolled out Okta users on react side
            if (isRolledOutOktaUser) {
              authLogout({
                logoutParams: { returnTo: `${window.location.origin}${appAuth0LogoutCallbackPath?.path}` }
              })
              // otherwise move the Azure user to TDF
            } else {
              gotoLegacyLogin()
            }
          } else {
            appTypeLoginEntryPath.goto(undefined, false, returnToUrl)
          }
          break
        case LogoutTypes.impersonatedEnduserLogout:
          gotoAdminEntryPathFromEnduser(allRoutesConfig.USERS_LIST.uri())
      }
    }
  }, [
    appLogoutIsFinished,
    authLogout,
    dispatch,
    generateQNDigestReturnUrlParams,
    appTypeLoginEntryPath,
    isPdDomainIdIsSet
  ])

  const logout = useCallback(
    (useTDFTransition: boolean) => {
      // F10: Determine if this is a Login App session (only for admin/wizard, not enduser/cudaspt)
      // Fallback to loginAppEnabled ONLY when accessTokenObject is absent (early auth error before token loaded).
      // If accessTokenObject exists but authMode is missing, that's a SOAP session - don't use fallback.
      const isAdminOrWizardApp = appConfig.APP.NAME === AppNames.admin || appConfig.APP.NAME === AppNames.wizard
      const isLoginAppSession =
        isAdminOrWizardApp && (authMode === 'login_app' || (!accessTokenObject && loginAppEnabled))

      // F10: For Login App sessions, redirect directly to /v2/auth-service/logout.
      // Don't call DELETE /v2/user/session or removeSessionCookie() - the logout endpoint
      // needs the session cookie to read login_app_session_secret for refresh token cleanup.
      // The endpoint handles all cleanup and expires the cookie in its 302 response.
      if (isLoginAppSession) {
        logger.info('Login App logout - redirecting directly to /v2/auth-service/logout')
        dispatch(resetLogout())
        clearAllBccStorage()
        gotoLoginAppLogout()
        return
      }

      logoutParameters = [
        LogoutTypes.logout,
        useTDFTransition,
        isAzureAdAccountStored,
        isMyFeatureOn(FEATURES.EGD_React_Okta_Rollout),
        generateQNDigestReturnUrlParams(),
        !!(roleType && roleType === UserRole.ACCOUNT_USER) || isAccountHelpdeskUser,
        isEnhancedAuthenticationEnabled,
        false // isLoginAppAuth - always false here since Login App handled above
      ]
      logger.info(`Logout called with params ${logoutParameters}`)
      dispatch(appLogout())
    },
    [
      dispatch,
      isAzureAdAccountStored,
      generateQNDigestReturnUrlParams,
      roleType,
      isEnhancedAuthenticationEnabled,
      isAccountHelpdeskUser,
      accessTokenObject,
      authMode,
      loginAppEnabled
    ]
  )

  const logoutImpersonatedEnduser = useCallback(() => {
    logoutParameters = [LogoutTypes.impersonatedEnduserLogout, false, false, false, undefined, false, undefined, false]
    dispatch(appLogout())
  }, [dispatch])

  return useMemo(() => [logout, logoutImpersonatedEnduser], [logout, logoutImpersonatedEnduser])
}

export default useLogout
